import { useCallback } from 'react';
import * as THREE from 'three';
import { IBuildingProviderContext } from '../../../@types/ContextProviderRelated';
import { EDirection, EUnitType, EWall } from '../../../@types/Enums';
import { snapRadiatorToWall } from '../../../components/three/Radiator';
import { EHistoryAction } from '../../../hooks/useUndoRedo';
import { IBoundaryEdge } from '../../../models/abstract/BoundaryEdge';
import { IWall } from '../../../models/abstract/Wall';
import AttributeModel from '../../../models/standard/AttributeModel';
import PartitionWallModel from '../../../models/standard/PartitionWallModel';
import PhysicalBoundaryModel, { BoundaryEdge } from '../../../models/standard/PhysicalBoundary';
import RadiatorModel from '../../../models/standard/RadiatorModel';
import RoomModel from '../../../models/standard/RoomModel';
import SpaceModel from '../../../models/standard/SpaceModel';
import StandardWallModel, { IStandardWall } from '../../../models/standard/StandardWallModel';
import { updateWallGeometryAttributes } from '../../../utils/attributes/attributesForObjectLoader';
import { getDefaultAttributesForObject, ObjectType } from '../../../utils/attributes/defaultObjectsAndAttributes';
import { calculateBoundaryIndicatorSegments, calculateBoundaryPolygonOrientation, orderBoundaryEdges } from '../../../utils/boundaryGeometryUtils';
import { cmToInternalRounded } from '../../../utils/conversionUtils';
import {
  cleanupRemainingRoomsForRemovedWalls,
  collectBuildingEdgesForWallHelper,
  finalizePartitionCore,
  isWallShared,
  mergeRoomsByIndices,
  mergeSpacesAcrossDeletedWall,
  removeWallFromBuildingHelper,
  wallsReferencedByRoom
} from '../../../utils/multiroomUtils';
import { adjustWallLength, calculateUnitPerpendicular, calculateWallShape, updateConnectedWallLengths } from '../../../utils/vectorUtils';
import { useUnitContext } from '../../UnitProvider';

/**
 * ---------- Local helpers ----------
 */

const EPSILON = 0.001;
const isSame = (p1: THREE.Vector3, p2: THREE.Vector3) => p1.distanceTo(p2) < EPSILON;

const GHOST_EPS = 1e-4;
const isSame2D = (a?: THREE.Vector3, b?: THREE.Vector3, eps = GHOST_EPS): boolean => {
  if (!a || !b) return false;
  return Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps;
};

const getPointSafe = (w: any, key: 'startPoint' | 'endPoint'): THREE.Vector3 | undefined => {
  const p = w?.[key];
  if (!p) return undefined;
  if (p instanceof THREE.Vector3) return p as THREE.Vector3;
  if (typeof p.x === 'number' && typeof p.y === 'number') {
    return new THREE.Vector3(p.x, p.y, p.z ?? 0);
  }
  return undefined;
};

const collectRadiatorsFromStandard = (wall: StandardWallModel): RadiatorModel[] => {
  const out: RadiatorModel[] = [];

  const flat = (wall as any).radiators;
  if (Array.isArray(flat)) {
    out.push(...flat);
  }

  const sides: any = (wall as any).sides;
  if (sides) {
    (['A', 'B'] as const).forEach((key) => {
      const side = sides[key];
      if (side && Array.isArray(side.radiators)) {
        out.push(...side.radiators);
      }
    });
  }

  return out;
};

const convertGhostStandardWallsAfterMerge = (building: IBuildingProviderContext): IBuildingProviderContext => {
  const allWalls = (building.walls ?? []) as IWall[];
  if (!allWalls.length) return building;

  const newWalls: IWall[] = [];
  const replacementByUuid = new Map<string, IWall>();

  const endpointConnected = (pt: THREE.Vector3 | undefined, selfUuid: string): boolean => {
    if (!pt) return false;
    return allWalls.some((other) => {
      if (!other || other.uuid === selfUuid) return false;
      const os = getPointSafe(other, 'startPoint');
      const oe = getPointSafe(other, 'endPoint');
      return isSame2D(pt, os) || isSame2D(pt, oe);
    });
  };

  for (const w of allWalls) {
    if (!(w instanceof StandardWallModel)) {
      newWalls.push(w);
      continue;
    }

    const sPt = getPointSafe(w, 'startPoint');
    const ePt = getPointSafe(w, 'endPoint');

    if (!sPt || !ePt) {
      newWalls.push(w);
      continue;
    }

    const startConnected = endpointConnected(sPt, w.uuid);
    const endConnected = endpointConnected(ePt, w.uuid);

    if (startConnected && endConnected) {
      newWalls.push(w);
      continue;
    }

    const windows = (w as any).windows ?? [];
    const doors = (w as any).doors ?? [];
    const passages = (w as any).passages ?? [];
    const attributes = (w as any).attributes ?? [];
    const radiators = collectRadiatorsFromStandard(w);

    const pWall = new PartitionWallModel(
      sPt.clone(),
      ePt.clone(),
      (w as any).depth,
      windows,
      doors,
      radiators,
      attributes,
      w.uuid,
      passages,
    );

    (pWall as any).visible = (w as any).visible;
    if ((w as any).userData) {
      (pWall as any).userData = { ...(w as any).userData };
    }

    if (typeof (pWall as any).updateWallLengths === 'function') {
      (pWall as any).updateWallLengths();
    }

    replacementByUuid.set(w.uuid, pWall);
    newWalls.push(pWall);
  }

  if (!replacementByUuid.size) {
    return building;
  }

  const newRooms = (building.rooms ?? []).map((room) => {
    const newSpaces = room.spaces.map((space) => {
      let spaceChanged = false;

      const newSpaceWalls = space.walls.map((w) => {
        const repl = replacementByUuid.get(w.uuid);
        if (repl) {
          spaceChanged = true;
          return repl;
        }
        return w;
      });

      const newBoundaries = space.boundaries.map((boundary) => {
        if (boundary instanceof PhysicalBoundaryModel) {
          let boundaryChanged = false;

          const newBWalls = boundary.walls.map((w) => {
            const repl = replacementByUuid.get(w.uuid);
            if (repl) {
              boundaryChanged = true;
              return repl;
            }
            return w;
          });

          const newEdges = boundary.edges.map((edge) => {
            const repl = replacementByUuid.get(edge.wall.uuid);
            if (repl) {
              boundaryChanged = true;
              edge.wall = repl;
              return edge;
            }
            return edge;
          });

          if (boundaryChanged) {
            spaceChanged = true;
            boundary.setWalls(newBWalls);
            boundary.setEdges(newEdges);
            return boundary;
          }
        }
        return boundary;
      });

      if (spaceChanged) {
        const newSpace = new SpaceModel(space.floor, space.products, space.elements, space.models2D, newBoundaries, newSpaceWalls);
        if ((space as any).uuid) newSpace.setUUID((space as any).uuid);
        return newSpace;
      }

      return space;
    });

    room.spaces = newSpaces;
    return room;
  });

  return {
    ...building,
    walls: newWalls,
    rooms: newRooms,
  };
};

export interface IWallActions {
  setBuildingWalls: (updateFunction: (walls: IWall[]) => IWall[], action?: EHistoryAction) => void;
  deleteWall: (
    wallIndex: number,
    opts?: {
      behavior?: 'auto' | 'mergeRooms' | 'deleteRoom' | 'justWall';
      targetRoomIdx?: number;
    },
  ) => void;
  updateWallThickness: (wallIndex: number, newThickness: number, direction?: EDirection) => void; 
  updateWallLength: (wallIndex: number, newLength: number, changeDirection: EDirection) => void;
  updatePartitionWallLength: (partitionWallIndex: number, newLength: number) => void;
  updatePartitionWallThickness: (partitionWallIndex: number, newThickness: number) => void;
  deletePartitionWall: (partitionWallIndex: number) => void;
  updateAllWallLengths: (newLengths: number[]) => void;
  addWallNode: (wallIndex: number) => void;
  updateStandardWallAttributes: (wallIndex: number, attributes: AttributeModel[]) => void;
  updatePartitionWallAttributes: (partitionWallIndex: number, attributes: AttributeModel[]) => void;
  setWallGeometryAndTypeAttributes: (wallIndex: number, unitType: EUnitType) => void;
  setPartitionWallGeometryAndTypeAttributes: (partitionWallIndex: number, unitType: EUnitType) => void;
  finalizePartitionWallIfSplitsRoom: (partitionWallIndex: number) => void;
  finalizePartitionWallIfSplitsRoomByUuid: (partitionWallUuid: string) => void;
}

function getWallLengthEndpoints(wall: StandardWallModel, centerLength: number, changeDirection: EDirection) {
  const startPt = wall.startPoint.clone();
  const endPt = wall.endPoint.clone();
  const currentCenterLength = startPt.distanceTo(endPt);
  const wallDir = currentCenterLength > 1e-9 ? new THREE.Vector3().subVectors(endPt, startPt).normalize() : new THREE.Vector3(1, 0, 0);

  if (changeDirection === EDirection.RIGHT) {
    return {
      start: startPt,
      end: startPt.clone().addScaledVector(wallDir, centerLength),
    };
  }

  if (changeDirection === EDirection.LEFT) {
    return {
      start: endPt.clone().addScaledVector(wallDir.clone().negate(), centerLength),
      end: endPt,
    };
  }

  const mid = startPt.clone().lerp(endPt, 0.5);
  const halfLen = centerLength / 2;

  return {
    start: mid.clone().addScaledVector(wallDir.clone().negate(), halfLen),
    end: mid.clone().addScaledVector(wallDir, halfLen),
  };
}

function buildWallLengthCandidateWalls(allWalls: IWall[], wallIndex: number, centerLength: number, changeDirection: EDirection): IWall[] {
  const targetWall = allWalls[wallIndex];

  if (!(targetWall instanceof StandardWallModel)) return allWalls;

  const startPt = targetWall.startPoint.clone();
  const endPt = targetWall.endPoint.clone();
  const { start: newStart, end: newEnd } = getWallLengthEndpoints(targetWall, centerLength, changeDirection);

  return allWalls.map((w, i) => {
    if (i === wallIndex) {
      return new StandardWallModel(newStart.clone(), newEnd.clone(), targetWall.depth, targetWall.windows, targetWall.doors, [], [...(targetWall.attributes ?? [])], targetWall.uuid, targetWall.passages);
    }

    if (!(w instanceof StandardWallModel)) return w;

    let wasModified = false;
    const wStart = w.startPoint.clone();
    const wEnd = w.endPoint.clone();

    if (changeDirection !== EDirection.RIGHT) {
      if (isSame(w.startPoint, startPt)) {
        wStart.copy(newStart);
        wasModified = true;
      } else if (isSame(w.endPoint, startPt)) {
        wEnd.copy(newStart);
        wasModified = true;
      }
    }

    if (changeDirection !== EDirection.LEFT) {
      if (isSame(w.startPoint, endPt)) {
        wStart.copy(newEnd);
        wasModified = true;
      } else if (isSame(w.endPoint, endPt)) {
        wEnd.copy(newEnd);
        wasModified = true;
      }
    }

    return wasModified ? new StandardWallModel(wStart, wEnd, w.depth, w.windows, w.doors, [], [...(w.attributes ?? [])], w.uuid, w.passages) : w;
  });
}

function measureCandidateBoundarySideLength(candidateWalls: IWall[], rooms: RoomModel[], wallUuid: string): number | null {
  const wallMap = new Map(candidateWalls.filter((wall): wall is StandardWallModel => wall instanceof StandardWallModel).map((wall) => [wall.uuid, wall] as const));
  let sideBLength: number | null = null;

  for (const room of rooms) {
    for (const space of room.spaces ?? []) {
      for (const boundary of space.boundaries ?? []) {
        if (!(boundary instanceof PhysicalBoundaryModel)) continue;

        const edges = (boundary.edges ?? []).map((edge) => {
          const clonedEdge = Object.assign(Object.create(Object.getPrototypeOf(edge)), edge) as IBoundaryEdge;
          const replacementWall = wallMap.get(edge.wall.uuid);

          if (replacementWall) clonedEdge.wall = replacementWall;
          clonedEdge.resetPoints?.();

          return clonedEdge;
        });

        const orderedEdges = orderBoundaryEdges(edges);
        if (orderedEdges.length !== edges.length) continue;

        const polygonOrientation = calculateBoundaryPolygonOrientation(orderedEdges);
        const segments = calculateBoundaryIndicatorSegments(orderedEdges, polygonOrientation);
        if (segments.length !== orderedEdges.length) continue;

        for (const segment of segments) {
          if (segment.edge.wall.uuid !== wallUuid) continue;

          const length = segment.startPoint.distanceTo(segment.endPoint);
          if (segment.edge.side === 'A') return length;
          if (segment.edge.side === 'B' && sideBLength === null) sideBLength = length;
        }
      }
    }
  }

  return sideBLength;
}

function refreshBoundaryEdgeDisplayPoints(edges: IBoundaryEdge[]) {
  edges.forEach((edge) => edge.resetPoints());

  const orderedEdges = orderBoundaryEdges(edges);
  if (orderedEdges.length !== edges.length) return;

  const polygonOrientation = calculateBoundaryPolygonOrientation(orderedEdges);
  const segments = calculateBoundaryIndicatorSegments(orderedEdges, polygonOrientation);
  if (segments.length !== orderedEdges.length) return;

  segments.forEach((segment) => {
    segment.edge.setPoints(segment.startPoint.clone(), segment.endPoint.clone());
  });
}

function measureCandidateSideALength(allWalls: IWall[], rooms: RoomModel[], wallIndex: number, centerLength: number, changeDirection: EDirection): number {
  const candidateWalls = buildWallLengthCandidateWalls(allWalls, wallIndex, centerLength, changeDirection);
  const candidateWall = candidateWalls[wallIndex];

  if (!(candidateWall instanceof StandardWallModel)) return centerLength;

  const boundaryLength = measureCandidateBoundarySideLength(candidateWalls, rooms, candidateWall.uuid);
  if (boundaryLength !== null) return boundaryLength;

  const geometry = calculateWallShape(candidateWall.startPoint, candidateWall.endPoint, candidateWall.depth, candidateWalls, wallIndex);

  return geometry.pointC2.distanceTo(geometry.pointD2);
}

function computeTargetCenterLength(allWalls: IWall[], rooms: RoomModel[], wallIndex: number, desiredSideALength: number, changeDirection: EDirection): number {
  const wall = allWalls[wallIndex];

  if (!(wall instanceof StandardWallModel)) return desiredSideALength;

  const currentCenterLength = wall.startPoint.distanceTo(wall.endPoint);
  if (currentCenterLength < 1e-9) return desiredSideALength;

  let centerLength = Math.max(0.001, currentCenterLength + desiredSideALength - measureCandidateSideALength(allWalls, rooms, wallIndex, currentCenterLength, changeDirection));

  for (let iter = 0; iter < 8; iter++) {
    const measuredSideA = measureCandidateSideALength(allWalls, rooms, wallIndex, centerLength, changeDirection);
    const error = measuredSideA - desiredSideALength;

    if (Math.abs(error) < 1e-5) break;

    const probeStep = Math.max(0.001, Math.abs(centerLength) * 1e-4);
    const probeSideA = measureCandidateSideALength(allWalls, rooms, wallIndex, centerLength + probeStep, changeDirection);
    const slope = (probeSideA - measuredSideA) / probeStep;

    centerLength -= Number.isFinite(slope) && Math.abs(slope) > 1e-6 ? error / slope : error;
    centerLength = Math.max(0.001, centerLength);
  }

  return centerLength;
}

export const useWallActions = (setBuilding: React.Dispatch<React.SetStateAction<IBuildingProviderContext>>): IWallActions => {
  const { unitType } = useUnitContext();

  const setBuildingWalls = useCallback(
    (updateFn: (walls: IWall[]) => IWall[], action?: EHistoryAction) => {
      setBuilding((prev) => ({
        ...prev,
        walls: updateFn(prev.walls ?? []),
        isSetByUndoRedo: false,
        lastAction: action ?? EHistoryAction.WALLS_CHANGED,
      }));
    },
    [setBuilding],
  );

  const deleteWall = useCallback(
    (
      wallIndex: number,
      opts?: {
        behavior?: 'auto' | 'mergeRooms' | 'deleteRoom' | 'justWall';
        targetRoomIdx?: number;
      },
    ) => {
      setBuilding((prev) => {
        const stdIndices = (prev.walls ?? []).map((w, i) => (w instanceof StandardWallModel ? i : -1)).filter((i): i is number => i !== -1);

        const actualIndex = stdIndices[wallIndex] ?? wallIndex;
        const target = prev.walls?.[actualIndex];
        if (!target) return prev;

        const refsBeforeRemoval = collectBuildingEdgesForWallHelper(prev, target.uuid);
        const { shared, roomIdxSet } = isWallShared(prev, target.uuid);

        let behavior: 'mergeRooms' | 'deleteRoom' | 'justWall';
        if (opts?.behavior && opts.behavior !== 'auto') {
          behavior = opts.behavior;
        } else {
          behavior = shared ? 'mergeRooms' : 'justWall';
        }

        if (behavior === 'mergeRooms') {
          const afterRemoval = removeWallFromBuildingHelper(prev, target.uuid);

          const targetRoomIdxOverride = opts?.targetRoomIdx;
          let next = mergeSpacesAcrossDeletedWall(afterRemoval, refsBeforeRemoval, targetRoomIdxOverride);

          if (next === afterRemoval) {
            const roomIndices = Array.from(roomIdxSet.values());
            if (roomIndices.length >= 2) {
              const indices = [...roomIndices];
              if (typeof targetRoomIdxOverride === 'number' && indices.includes(targetRoomIdxOverride)) {
                indices.sort((a, b) => a - b);
                const ordered = [targetRoomIdxOverride, ...indices.filter((i) => i !== targetRoomIdxOverride)];
                next = mergeRoomsByIndices(afterRemoval, ordered);
              } else {
                next = mergeRoomsByIndices(afterRemoval, roomIndices);
              }
            }
          }

          const cleaned = convertGhostStandardWallsAfterMerge(next);

          return {
            ...cleaned,
            isSetByUndoRedo: false,
            lastAction: EHistoryAction.WALL_DELETED,
          };
        }

        if (behavior === 'deleteRoom') {
          const victimRoomIdx = refsBeforeRemoval.length ? refsBeforeRemoval[0].ref.roomIdx : -1;
          if (victimRoomIdx < 0 || !prev.rooms?.[victimRoomIdx]) {
            const next = removeWallFromBuildingHelper(prev, target.uuid);
            return { ...next, isSetByUndoRedo: false, lastAction: EHistoryAction.WALL_DELETED };
          }
          const victimRoom = prev.rooms[victimRoomIdx] as RoomModel;
          const victimWalls = wallsReferencedByRoom(victimRoom);
          const remainingRooms = prev.rooms.filter((_, i) => i !== victimRoomIdx);
          const stillUsedWalls = new Set<string>();
          remainingRooms.forEach((r) => wallsReferencedByRoom(r as RoomModel).forEach((u) => stillUsedWalls.add(u)));
          const toRemove = new Set(Array.from(victimWalls).filter((u) => !stillUsedWalls.has(u)));
          const remainingWalls = (prev.walls ?? []).filter((w) => !toRemove.has(w.uuid));
          const cleanedRooms = cleanupRemainingRoomsForRemovedWalls(remainingRooms as RoomModel[], toRemove, remainingWalls as IWall[]);
          return {
            ...prev,
            walls: remainingWalls,
            rooms: cleanedRooms,
            isSetByUndoRedo: false,
            lastAction: EHistoryAction.WALL_DELETED,
          };
        }

        const next = removeWallFromBuildingHelper(prev, target.uuid);
        return {
          ...next,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.WALL_DELETED,
        };
      });
    },
    [setBuilding],
  );

  const updateWallThickness = useCallback(
    (wallIndex: number, newThickness: number, direction: EDirection = EDirection.LEFT) => {
      setBuilding((prev) => {
        const allWalls = prev.walls ?? [];
        const targetWall = allWalls[wallIndex];

        if (!targetWall || !(targetWall instanceof StandardWallModel)) return prev;

        const oldThickness = targetWall.depth;
        const thicknessDelta = newThickness - oldThickness;
        let shiftVector = new THREE.Vector3(0, 0, 0);

        if (thicknessDelta !== 0 && direction !== EDirection.LEFT) {
          const vectorAB = new THREE.Vector2(targetWall.endPoint.x - targetWall.startPoint.x, targetWall.endPoint.y - targetWall.startPoint.y);
          const normal2D = calculateUnitPerpendicular(vectorAB);
          const normal3D = new THREE.Vector3(normal2D.x, normal2D.y, 0);

          let shiftScalar = 0;
          if (direction === EDirection.RIGHT) {
            shiftScalar = -thicknessDelta;
          } else if (direction === EDirection.BOTH) {
            shiftScalar = -thicknessDelta / 2;
          }
          shiftVector = normal3D.multiplyScalar(shiftScalar);
        }

        const oldStart = targetWall.startPoint.clone();
        const oldEnd = targetWall.endPoint.clone();
        const newStart = oldStart.clone().add(shiftVector);
        const newEnd = oldEnd.clone().add(shiftVector);

        const nextWalls = allWalls.map((w, i) => {
          if (i === wallIndex) {
            const newWall = new StandardWallModel(newStart.clone(), newEnd.clone(), newThickness, w.windows, w.doors, [], [...(w.attributes ?? [])], w.uuid, w.passages);
            const wall = w as StandardWallModel;

            const vecA = new THREE.Vector2(newStart.x, newStart.y);
            const vecB = new THREE.Vector2(newEnd.x, newEnd.y);
            const vecAB = vecB.clone().sub(vecA);
            const unitPerp = calculateUnitPerpendicular(vecAB).multiplyScalar(newThickness);

            const pC = new THREE.Vector3(vecA.x + unitPerp.x, vecA.y + unitPerp.y, 0);
            const pD = new THREE.Vector3(vecB.x + unitPerp.x, vecB.y + unitPerp.y, 0);

            newWall.setOuterPoints(pC, pD);
            newWall.setOuterPoints2(wall.outerStartPoint2.clone(), wall.outerEndPoint2.clone());

            const oneCm = cmToInternalRounded(1);
            const maxNiche = Math.max(0, newThickness - oneCm);
            const snapRadiators = (radiators: RadiatorModel[], side: 'A' | 'B') => radiators.map((r) => {
                const newR = new RadiatorModel(r.position.clone(), r.length, r.depth, r.angle, [...(r.attributes ?? [])], r.uuid, r.nicheDepth);
                if (newR.nicheDepth > maxNiche) newR.nicheDepth = maxNiche;
                const snapped = snapRadiatorToWall(newWall, newR.position.clone(), newR.length, newR.depth, newR.nicheDepth, side);
                newR.position.copy(snapped);
                return newR;
              });
            newWall.sides.A.radiators = snapRadiators(w.sides.A.radiators, 'A');
            newWall.sides.B.radiators = snapRadiators(w.sides.B.radiators, 'B');
            newWall.updateWallLengths();
            return newWall;
          }

          if (w instanceof StandardWallModel) {
            const pStart = w.startPoint.clone();
            const pEnd = w.endPoint.clone();
            let wasModified = false;

            if (isSame(w.startPoint, oldStart)) {
              pStart.copy(newStart);
              wasModified = true;
            } else if (isSame(w.endPoint, oldStart)) {
              pEnd.copy(newStart);
              wasModified = true;
            }

            if (isSame(w.startPoint, oldEnd)) {
              pStart.copy(newEnd);
              wasModified = true;
            } else if (isSame(w.endPoint, oldEnd)) {
              pEnd.copy(newEnd);
              wasModified = true;
            }

            if (wasModified) {
              const neighbor = new StandardWallModel(pStart, pEnd, w.depth, w.windows, w.doors, [], w.attributes, w.uuid, w.passages);
              neighbor.sides.A.radiators = [...w.sides.A.radiators];
              neighbor.sides.B.radiators = [...w.sides.B.radiators];
              neighbor.updateWallLengths();
              return neighbor;
            }
          }
          return w;
        });

        const wallMap = new Map<string, IStandardWall>();
        nextWalls.forEach((w) => {
          if (w instanceof StandardWallModel) wallMap.set(w.uuid, w);
        });

        const newRooms = prev.rooms.map((room) => {
          const newSpaces = room.spaces.map((space) => {
            let spaceChanged = false;

            const newSpaceWalls = space.walls.map((w) => {
              if (wallMap.has(w.uuid)) {
                spaceChanged = true;
                return wallMap.get(w.uuid)!;
              }
              return w;
            });

            const newBoundaries = space.boundaries.map((boundary) => {
              if (boundary instanceof PhysicalBoundaryModel) {
                let boundaryChanged = false;

                const newBWalls = boundary.walls.map((w) => {
                  if (wallMap.has(w.uuid)) {
                    boundaryChanged = true;
                    return wallMap.get(w.uuid)!;
                  }
                  return w;
                });

                const newEdges = boundary.edges.map((edge) => {
                  if (wallMap.has(edge.wall.uuid)) {
                    boundaryChanged = true;
                    const newWallRef = wallMap.get(edge.wall.uuid)!;

                    edge.wall = newWallRef;
                    return edge;
                  }
                  return edge;
                });

                if (boundaryChanged) {
                  spaceChanged = true;
                  boundary.setWalls(newBWalls);
                  boundary.setEdges(newEdges);
                  return boundary;
                }
              }
              return boundary;
            });

            if (spaceChanged) {
              const newSpace = new SpaceModel(space.floor, space.products, space.elements, space.models2D, newBoundaries, newSpaceWalls);
              if (space.uuid) newSpace.setUUID(space.uuid);
              return newSpace;
            }
            return space;
          });
          room.spaces = newSpaces;
          return room;
        });

        return {
          ...prev,
          walls: nextWalls,
          rooms: newRooms,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.WALL_THICKNESS_CHANGED,
        };
      });
    },
    [setBuilding],
  );
  
const updateWallLength = useCallback(
  (wallIndex: number, newInnerLengthInternal: number, changeDirection: EDirection) => {
    setBuilding((prev) => {
      const allWalls = prev.walls ?? [];
      const targetWall = allWalls[wallIndex];

      if (!targetWall || !(targetWall instanceof StandardWallModel)) return prev;

      const desiredInnerLengthMeters = newInnerLengthInternal;

      const newCenterLengthMeters = computeTargetCenterLength(allWalls, prev.rooms ?? [], wallIndex, desiredInnerLengthMeters, changeDirection);

      const startPt = targetWall.startPoint.clone();
      const endPt = targetWall.endPoint.clone();
      const currentCenterLength = startPt.distanceTo(endPt);

      const wallDir = currentCenterLength > 1e-9
        ? new THREE.Vector3().subVectors(endPt, startPt).normalize()
        : new THREE.Vector3(1, 0, 0);

      let newStart = startPt.clone();
      let newEnd = endPt.clone();


      if (changeDirection === EDirection.RIGHT) {
        newEnd = startPt.clone().addScaledVector(wallDir, newCenterLengthMeters);
      } else if (changeDirection === EDirection.LEFT) {
        newStart = endPt.clone().addScaledVector(wallDir.clone().negate(), newCenterLengthMeters);
      } else {
        const mid = startPt.clone().lerp(endPt, 0.5);
        const halfLen = newCenterLengthMeters / 2;
        newStart = mid.clone().addScaledVector(wallDir.clone().negate(), halfLen);
        newEnd = mid.clone().addScaledVector(wallDir, halfLen);
      }
      const nextWalls = allWalls.map((w, i) => {
        if (i === wallIndex) {
          const newWall = new StandardWallModel(
            newStart.clone(), newEnd.clone(), targetWall.depth,
            targetWall.windows, targetWall.doors, [], [...(targetWall.attributes ?? [])], targetWall.uuid, targetWall.passages
          );

          newWall.setOuterPoints(targetWall.getOuterStartPoint().clone(), targetWall.getOuterEndPoint().clone());
          newWall.setOuterPoints2(targetWall.getOuterStartPoint2().clone(), targetWall.getOuterEndPoint2().clone());
          
          const snapRads = (rads: RadiatorModel[], side: 'A' | 'B') => (rads ?? []).map(r => {
              const newR = new RadiatorModel(r.position.clone(), r.length, r.depth, r.angle, [...(r.attributes ?? [])], r.uuid, r.nicheDepth);
              const snapped = snapRadiatorToWall(newWall, newR.position.clone(), newR.length, newR.depth, newR.nicheDepth, side);
              newR.position.copy(snapped);
              return newR;
          });
          newWall.sides.A.radiators = snapRads(targetWall.sides.A.radiators, 'A');
          newWall.sides.B.radiators = snapRads(targetWall.sides.B.radiators, 'B');
          
          newWall.updateWallLengths();
          return newWall;
        }

        if (w instanceof StandardWallModel) {
          let wasModified = false;
          const wStart = w.startPoint.clone();
          const wEnd = w.endPoint.clone();

          if (changeDirection !== EDirection.RIGHT) {
             if (isSame(w.startPoint, startPt)) { wStart.copy(newStart); wasModified = true; }
             else if (isSame(w.endPoint, startPt)) { wEnd.copy(newStart); wasModified = true; }
          }
          if (changeDirection !== EDirection.LEFT) {
             if (isSame(w.startPoint, endPt)) { wStart.copy(newEnd); wasModified = true; }
             else if (isSame(w.endPoint, endPt)) { wEnd.copy(newEnd); wasModified = true; }
          }

          if (wasModified) {
            const neighbor = new StandardWallModel(
              wStart, wEnd, w.depth, w.windows, w.doors, [], [...(w.attributes ?? [])], w.uuid, w.passages
            );
            
            // Radiatoren des Nachbarn übernehmen
            const snapNeighborRads = (rads: RadiatorModel[], side: 'A' | 'B') => (rads ?? []).map(r => {
                const newR = new RadiatorModel(r.position.clone(), r.length, r.depth, r.angle, [...(r.attributes ?? [])], r.uuid, r.nicheDepth);
                const snapped = snapRadiatorToWall(neighbor, newR.position.clone(), newR.length, newR.depth, newR.nicheDepth, side);
                newR.position.copy(snapped);
                return newR;
            });
            neighbor.sides.A.radiators = snapNeighborRads(w.sides.A.radiators, 'A');
            neighbor.sides.B.radiators = snapNeighborRads(w.sides.B.radiators, 'B');

            neighbor.updateWallLengths();
            return neighbor;
          }
        }
        return w;
      });

      // 5. Referenzen in Räumen und Boundaries aktualisieren (verhindert Ghost-Walls)
      const wallMap = new Map<string, IStandardWall>();
      nextWalls.forEach((w) => {
        if (w instanceof StandardWallModel) wallMap.set(w.uuid, w);
      });

      const newRooms = prev.rooms.map((room) => {
        const newSpaces = room.spaces.map((space) => {
          let spaceChanged = false;

          const newSpaceWalls = space.walls.map((w) => {
            if (wallMap.has(w.uuid)) { spaceChanged = true; return wallMap.get(w.uuid)!; }
            return w;
          });

          const newBoundaries = space.boundaries.map((boundary) => {
            if (boundary instanceof PhysicalBoundaryModel) {
              let boundaryChanged = false;
              
              const newBWalls = boundary.walls.map((w) => {
                if (wallMap.has(w.uuid)) { boundaryChanged = true; return wallMap.get(w.uuid)!; }
                return w;
              });

              const newEdges = boundary.edges.map((edge) => {
                const newEdge = Object.assign(Object.create(Object.getPrototypeOf(edge)), edge) as IBoundaryEdge;
                const replacementWall = wallMap.get(edge.wall.uuid);

                if (replacementWall) {
                  boundaryChanged = true;
                  newEdge.wall = replacementWall;
                }

                return newEdge;
              });

              if (boundaryChanged) {
                spaceChanged = true;
                refreshBoundaryEdgeDisplayPoints(newEdges);
                boundary.setWalls(newBWalls);
                boundary.setEdges(newEdges);
              }
            }
            return boundary;
          });

          if (spaceChanged) {
            const newSpace = new SpaceModel(space.floor, space.products, space.elements, space.models2D, newBoundaries, newSpaceWalls);
            if ((space as any).uuid) newSpace.setUUID((space as any).uuid);
            return newSpace;
          }
          return space;
        });
        room.spaces = newSpaces;
        return room;
      });

      return {
        ...prev,
        walls: nextWalls,
        rooms: newRooms,
        isSetByUndoRedo: false,
        lastAction: EHistoryAction.WALL_LENGTH_CHANGED,
      };
    });
  },
  [setBuilding]
);
  const updatePartitionWallLength = useCallback(
    (partitionWallIndex: number, newLength: number) => {
      setBuilding((prev) => {
        const partIndices = (prev.walls ?? []).map((w, i) => (w.type === EWall.PARTITION ? i : -1)).filter((i): i is number => i !== -1);
        const actualIndex = partIndices[partitionWallIndex];
        if (actualIndex === null || actualIndex < 0) return prev;
        const walls = (prev.walls ?? []).map((w, i) => {
          if (i !== actualIndex || !(w instanceof PartitionWallModel)) return w;
          const [start, end] = adjustWallLength(w.startPoint, w.endPoint, newLength);
          w.startPoint.copy(start);
          w.endPoint.copy(end);
          if ((w as any).updateWallLengths) {
            (w as any).updateWallLengths();
          }
          return w;
        });
        return {
          ...prev,
          walls,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.WALL_PARTITION_LENGTH_CHANGED,
        };
      });
    },
    [setBuilding],
  );

  const updatePartitionWallThickness = useCallback(
    (partitionWallIndex: number, newThickness: number) => {
      setBuilding((prev) => {
        const partIndices = (prev.walls ?? []).map((w, i) => (w.type === EWall.PARTITION ? i : -1)).filter((i): i is number => i !== -1);
        const actualIndex = partIndices[partitionWallIndex];
        if (actualIndex === null || actualIndex < 0) return prev;
        const walls = (prev.walls ?? []).map((w, i) => {
          if (i !== actualIndex || !(w instanceof PartitionWallModel)) return w;
          w.depth = newThickness;
          if ((w as any).updateWallLengths) {
            (w as any).updateWallLengths();
          }
          return w;
        });
        return {
          ...prev,
          walls,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.WALL_PARTITION_THICKNESS_CHANGED,
        };
      });
    },
    [setBuilding],
  );

  const deletePartitionWall = useCallback(
    (partitionWallIndex: number) => {
      setBuilding((prev) => {
        const partIndices = (prev.walls ?? []).map((w, i) => (w.type === EWall.PARTITION ? i : -1)).filter((i): i is number => i !== -1);
        const actualIndex = partIndices[partitionWallIndex];
        const walls = prev.walls?.filter((_, i) => i !== actualIndex) ?? [];
        return {
          ...prev,
          walls,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.WALL_PARTITION_DELETED,
        };
      });
    },
    [setBuilding],
  );

  const updateAllWallLengths = useCallback(
    (newLengths: number[]) => {
      setBuilding((prev) => {
        const standards = (prev.walls ?? []).filter((w): w is StandardWallModel => w instanceof StandardWallModel);
        let updatedStandards = [...standards];
        newLengths.forEach((len, idx) => {
          updatedStandards = updateConnectedWallLengths(updatedStandards, idx, len);
        });
        let stdIdx = 0;
        const walls =
          prev.walls?.map((oldW) => {
            if (oldW instanceof StandardWallModel) {
              const updatedW = updatedStandards[stdIdx++];
              const freshWall = new StandardWallModel(
                updatedW.startPoint.clone(),
                updatedW.endPoint.clone(),
                updatedW.depth,
                updatedW.windows,
                updatedW.doors,
                [],
                updatedW.attributes ?? [],
                updatedW.uuid,
                oldW.passages,
              );
              freshWall.setOuterPoints(updatedW.getOuterStartPoint().clone(), updatedW.getOuterEndPoint().clone());
              freshWall.setOuterPoints2(updatedW.getOuterStartPoint2().clone(), updatedW.getOuterEndPoint2().clone());
              freshWall.sides.A.radiators = oldW.sides.A.radiators.map((r) => {
                const newR = new RadiatorModel(r.position.clone(), r.length, r.depth, r.angle, [...(r.attributes ?? [])], r.uuid, r.nicheDepth);
                const snapped = snapRadiatorToWall(freshWall, newR.position.clone(), newR.length, newR.depth, newR.nicheDepth, 'A');
                newR.position.copy(snapped);
                return newR;
              });
              freshWall.sides.B.radiators = oldW.sides.B.radiators.map((r) => {
                const newR = new RadiatorModel(r.position.clone(), r.length, r.depth, r.angle, [...(r.attributes ?? [])], r.uuid, r.nicheDepth);
                const snapped = snapRadiatorToWall(freshWall, newR.position.clone(), newR.length, newR.depth, newR.nicheDepth, 'B');
                newR.position.copy(snapped);
                return newR;
              });
              freshWall.updateWallLengths();
              return freshWall;
            }
            return oldW;
          }) ?? [];
        return {
          ...prev,
          walls,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.ALL_WALL_LENGTHS_UPDATED,
        };
      });
    },
    [setBuilding],
  );

  const updateStandardWallAttributes = useCallback(
    (wallIndex: number, attributes: AttributeModel[]) => {
      setBuilding((prev) => {
        const walls = [...(prev.walls ?? [])];
        const w = walls[wallIndex];
        if (!w) return prev;
        const defaultWallAttrs = getDefaultAttributesForObject(ObjectType.Wall);
        const updatedAttrs = attributes.map((attr) => {
          const def = defaultWallAttrs.find((d) => d.id === attr.id);
          return new AttributeModel(
            attr.id,
            attr.key,
            attr.value,
            attr.predecessorId,
            attr.type,
            attr.unit,
            attr.hidden ?? def?.hidden ?? false,
            attr.forced ?? def?.forced ?? false,
            attr.ownerId,
            attr.userId,
            attr.publicFlag,
            attr.localization,
            attr.defaults,
            attr.validation,
            attr.unitFamily,
          );
        });
        if (w instanceof StandardWallModel) {
          const newWall = new StandardWallModel(w.startPoint, w.endPoint, w.depth, w.windows, w.doors, [], updatedAttrs, w.uuid, w.passages);
          newWall.setOuterPoints(w.getOuterStartPoint(), w.getOuterEndPoint());
          newWall.setOuterPoints2(w.getOuterStartPoint2(), w.getOuterEndPoint2());
          newWall.sides.A.radiators = [...w.sides.A.radiators];
          newWall.sides.B.radiators = [...w.sides.B.radiators];
          newWall.updateWallLengths();
          walls[wallIndex] = newWall;
        } else if (w instanceof PartitionWallModel) {
          walls[wallIndex] = new PartitionWallModel(w.startPoint, w.endPoint, w.depth, w.windows, w.doors, [], updatedAttrs, w.uuid, w.passages);
          walls[wallIndex].sides.A.radiators = [...w.sides.A.radiators];
          walls[wallIndex].sides.B.radiators = [...w.sides.B.radiators];
        }
        return {
          ...prev,
          walls,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.WALL_STANDARD_ATTRIBUTE_UPDATED,
        };
      });
    },
    [setBuilding],
  );

  const updatePartitionWallAttributes = useCallback(
    (partitionWallIndex: number, attributes: AttributeModel[]) => {
      setBuilding((prev) => {
        const partIndices = (prev.walls ?? []).map((w, i) => (w.type === EWall.PARTITION ? i : -1)).filter((i): i is number => i !== -1);
        const actualIndex = partIndices[partitionWallIndex];
        if (actualIndex === undefined || actualIndex === null) return prev;
        const walls = [...(prev.walls ?? [])];
        const w = walls[actualIndex];
        if (!w) return prev;
        const defaultWallAttrs = getDefaultAttributesForObject(ObjectType.Wall);
        const updatedAttrs = attributes.map((attr) => {
          const def = defaultWallAttrs.find((d) => d.id === attr.id);
          return new AttributeModel(
            attr.id,
            attr.key,
            attr.value,
            attr.predecessorId,
            attr.type,
            attr.unit,
            attr.hidden ?? def?.hidden ?? false,
            attr.forced ?? def?.forced ?? false,
            attr.ownerId,
            attr.userId,
            attr.publicFlag,
            attr.localization,
            attr.defaults,
            attr.validation,
            attr.unitFamily,
          );
        });
        if (w instanceof PartitionWallModel) {
          walls[actualIndex] = new PartitionWallModel(w.startPoint, w.endPoint, w.depth, w.windows, w.doors, [], updatedAttrs, w.uuid, w.passages);
          walls[actualIndex].sides.A.radiators = [...w.sides.A.radiators];
          walls[actualIndex].sides.B.radiators = [...w.sides.B.radiators];
        }
        return {
          ...prev,
          walls,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.WALL_PARTITION_ATTRIBUTE_UPDATED,
        };
      });
    },
    [setBuilding],
  );

  const setWallGeometryAndTypeAttributes = useCallback(
    (wallIndex: number, unitType: EUnitType) => {
      setBuilding((prev) => {
        if (!prev.walls?.[wallIndex]) return prev;
        const walls = [...(prev.walls ?? [])];
        const currentWall = walls[wallIndex];
        if (!currentWall.attributes?.length) return prev;
        const updatedAttrs = updateWallGeometryAttributes(currentWall, unitType, currentWall.attributes as AttributeModel[]);
        if (currentWall instanceof StandardWallModel) {
          const newWall = new StandardWallModel(
            currentWall.startPoint,
            currentWall.endPoint,
            currentWall.depth,
            currentWall.windows,
            currentWall.doors,
            [],
            updatedAttrs,
            currentWall.uuid,
            currentWall.passages,
          );
          newWall.setOuterPoints(currentWall.getOuterStartPoint(), currentWall.getOuterEndPoint());
          newWall.setOuterPoints2(currentWall.getOuterStartPoint2(), currentWall.getOuterEndPoint2());
          newWall.sides.A.radiators = [...currentWall.sides.A.radiators];
          newWall.sides.B.radiators = [...currentWall.sides.B.radiators];
          newWall.updateWallLengths();
          walls[wallIndex] = newWall;
        } else if (currentWall instanceof PartitionWallModel) {
          walls[wallIndex] = new PartitionWallModel(
            currentWall.startPoint,
            currentWall.endPoint,
            currentWall.depth,
            currentWall.windows,
            currentWall.doors,
            [],
            updatedAttrs,
            currentWall.uuid,
            currentWall.passages,
          );
          walls[wallIndex].sides.A.radiators = [...currentWall.sides.A.radiators];
          walls[wallIndex].sides.B.radiators = [...currentWall.sides.B.radiators];
        }
        return {
          ...prev,
          walls,
          lastAction: EHistoryAction.WALL_STANDARD_GEOMETRY_ATTRIBUTE_UPDATED,
        };
      });
    },
    [setBuilding],
  );

  const setPartitionWallGeometryAndTypeAttributes = useCallback(
    (partitionWallIndex: number, unitType: EUnitType) => {
      setBuilding((prev) => {
        const partIndices = (prev.walls ?? []).map((w, i) => (w.type === EWall.PARTITION ? i : -1)).filter((i): i is number => i !== -1);
        const actualIndex = partIndices[partitionWallIndex];
        if (actualIndex === null || !prev.walls?.[actualIndex]) return prev;
        const walls = [...(prev.walls ?? [])];
        const currentWall = walls[actualIndex];
        if (!currentWall.attributes?.length || !(currentWall instanceof PartitionWallModel)) {
          return prev;
        }
        const updatedAttrs = updateWallGeometryAttributes(currentWall, unitType, currentWall.attributes as AttributeModel[]);
        currentWall.attributes = updatedAttrs;
        return {
          ...prev,
          walls,
          lastAction: EHistoryAction.WALL_PARTITION_GEOMETRY_ATTRIBUTE_UPDATED,
        };
      });
    },
    [setBuilding],
  );

  const addWallNode = useCallback(
    (wallIndex: number) => {
      setBuilding((prev) => {
        const target = prev.walls?.[wallIndex];
        if (!(target instanceof StandardWallModel)) return prev;

        const p1 = target.startPoint.clone();
        const p2 = target.endPoint.clone();
        const mid = p1.clone().lerp(p2, 0.5);

        const ab = p2.clone().sub(p1);
        const abLenSq = ab.lengthSq() || 1;

        const toVec3 = (v: any): THREE.Vector3 | undefined => {
          if (!v) return undefined;
          if (v instanceof THREE.Vector3) return v;
          if (typeof v.x === 'number' && typeof v.y === 'number') return new THREE.Vector3(v.x, v.y, v.z ?? 0);
          return undefined;
        };

        const getChildPoint = (c: any): THREE.Vector3 | undefined => {
          const pos = toVec3(c?.position);
          if (pos) return pos;

          const sp = toVec3(c?.startPoint) ?? toVec3(c?.start);
          const ep = toVec3(c?.endPoint) ?? toVec3(c?.end);
          if (sp && ep) return sp.clone().lerp(ep, 0.5);

          return undefined;
        };

        const getTOnWall = (pos: THREE.Vector3 | undefined): number => {
          if (!pos) return 0;
          return pos.clone().sub(p1).dot(ab) / abLenSq;
        };

        const splitByMid = <T>(items: T[] = [], pointGetter: (x: T) => THREE.Vector3 | undefined) => {
          const a: T[] = [];
          const b: T[] = [];
          items.forEach((it) => {
            const t = getTOnWall(pointGetter(it));
            if (t <= 0.5) a.push(it);
            else b.push(it);
          });
          return { a, b };
        };

        const retargetIfPresent = (items: any[], wallUuid: string) => {
          items.forEach((it) => {
            if (!it || typeof it !== 'object') return;
            (['wallUuid', 'wallUUID', 'wallId', 'parentWallUuid'] as const).forEach((k) => {
              if (k in it) (it as any)[k] = wallUuid;
            });
          });
        };

        const { a: w1Windows, b: w2Windows } = splitByMid<any>((target as any).windows ?? [], getChildPoint);
        const { a: w1Doors, b: w2Doors } = splitByMid<any>((target as any).doors ?? [], getChildPoint);
        const { a: w1Passages, b: w2Passages } = splitByMid<any>((target as any).passages ?? [], getChildPoint);

        const w1 = new StandardWallModel(p1.clone(), mid.clone(), target.depth, w1Windows, w1Doors, [], [...(target.attributes ?? [])], target.uuid, w1Passages);

        const w2 = new StandardWallModel(mid.clone(), p2.clone(), target.depth, w2Windows, w2Doors, [], [...(target.attributes ?? [])], undefined, w2Passages);

        retargetIfPresent(w1Windows, w1.uuid);
        retargetIfPresent(w1Doors, w1.uuid);
        retargetIfPresent(w1Passages, w1.uuid);
        retargetIfPresent(w2Windows, w2.uuid);
        retargetIfPresent(w2Doors, w2.uuid);
        retargetIfPresent(w2Passages, w2.uuid);

        const o1s = target.getOuterStartPoint().clone();
        const o1e = target.getOuterEndPoint().clone();
        const o1m = o1s.clone().lerp(o1e, 0.5);

        const o2s = target.getOuterStartPoint2().clone();
        const o2e = target.getOuterEndPoint2().clone();
        const o2m = o2s.clone().lerp(o2e, 0.5);

        w1.setOuterPoints(o1s, o1m);
        w2.setOuterPoints(o1m.clone(), o1e);

        w1.setOuterPoints2(o2s, o2m);
        w2.setOuterPoints2(o2m.clone(), o2e);

        const oneCm = cmToInternalRounded(1);
        const maxNiche = (depth: number) => Math.max(0, depth - oneCm);

        const splitRadiators = (rads: RadiatorModel[] = []) => {
          const a: RadiatorModel[] = [];
          const b: RadiatorModel[] = [];
          rads.forEach((r) => {
            const t = getTOnWall(r?.position);
            if (t <= 0.5) a.push(r);
            else b.push(r);
          });
          return { a, b };
        };

        const snapAll = (wall: StandardWallModel, rads: RadiatorModel[], side: 'A' | 'B') => {
          const max = maxNiche(wall.depth);
          return (rads ?? []).map((r) => {
            const nr = new RadiatorModel(r.position.clone(), r.length, r.depth, r.angle, [...(r.attributes ?? [])], r.uuid, r.nicheDepth);
            if (nr.nicheDepth > max) nr.nicheDepth = max;
            const snapped = snapRadiatorToWall(wall, nr.position.clone(), nr.length, nr.depth, nr.nicheDepth, side);
            nr.position.copy(snapped);
            return nr;
          });
        };

        const aSide = (target as any).sides?.A?.radiators ?? [];
        const bSide = (target as any).sides?.B?.radiators ?? [];

        const aSplit = splitRadiators(aSide);
        const bSplit = splitRadiators(bSide);

        w1.sides.A.radiators = snapAll(w1, aSplit.a, 'A');
        w2.sides.A.radiators = snapAll(w2, aSplit.b, 'A');

        w1.sides.B.radiators = snapAll(w1, bSplit.a, 'B');
        w2.sides.B.radiators = snapAll(w2, bSplit.b, 'B');

        w1.updateWallLengths();
        w2.updateWallLengths();

        const newGlobalWalls = [...(prev.walls ?? [])];
        const globalIdx = newGlobalWalls.findIndex((w) => w.uuid === target.uuid);
        if (globalIdx === -1) return prev;
        newGlobalWalls.splice(globalIdx, 1, w1, w2);

        const newRooms = (prev.rooms ?? []).map((room) => {
          const newSpaces = room.spaces.map((space) => {
            const spaceHasWall = space.walls.some((w) => w.uuid === target.uuid);
            let spaceChanged = false;

            const newBoundaries = (space.boundaries ?? []).map((b) => {
              if (!(b instanceof PhysicalBoundaryModel)) return b;

              const usesWall = b.edges.some((e) => e.wall.uuid === target.uuid);
              if (!usesWall) return b;

              spaceChanged = true;

              const newBWalls = b.walls.flatMap((w) => (w.uuid === target.uuid ? [w1, w2] : w));

              const newEdges: IBoundaryEdge[] = [];
              b.edges.forEach((edge) => {
                if (edge.wall.uuid === target.uuid) {
                  const e1 = new BoundaryEdge(w1, 0, 1, edge.side, edge.reversed);
                  const e2 = new BoundaryEdge(w2, 0, 1, edge.side, edge.reversed);
                  if (!edge.reversed) newEdges.push(e1, e2);
                  else newEdges.push(e2, e1);
                } else {
                  newEdges.push(edge);
                }
              });

              b.setWalls(newBWalls);
              b.setEdges(newEdges);
              return b;
            });

            if (!spaceHasWall && !spaceChanged) return space;

            const newSpaceWalls = space.walls.flatMap((w) => (w.uuid === target.uuid ? [w1, w2] : w));

            const newSpace = new SpaceModel(space.floor, space.products, space.elements, space.models2D, newBoundaries, newSpaceWalls);
            if ((space as any).uuid) newSpace.setUUID((space as any).uuid);
            return newSpace;
          });

          const newRoom = new RoomModel(room.title, newSpaces, room.attributes);
          if ((room as any).uuid && (newRoom as any).setUUID) (newRoom as any).setUUID((room as any).uuid);
          return newRoom;
        });

        return {
          ...prev,
          walls: newGlobalWalls,
          rooms: newRooms,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.NODE_ADDED,
        };
      });
    },
    [setBuilding],
  );

  const finalizePartitionWallIfSplitsRoom = useCallback(
    (partitionWallIndex: number) => {
      setBuilding((prev) => {
        const partIndices = (prev.walls ?? []).map((w, i) => (w?.type === EWall.PARTITION ? i : -1)).filter((i): i is number => i !== -1);
        const gi = partIndices[partitionWallIndex] ?? partitionWallIndex;
        if (gi < 0 || gi >= (prev.walls?.length ?? 0)) return prev;
        return finalizePartitionCore(prev, gi, unitType);
      });
    },
    [setBuilding],
  );

  const finalizePartitionWallIfSplitsRoomByUuid = useCallback(
    (partitionWallUuid: string) => {
      setBuilding((prev) => {
        const gi = (prev.walls ?? []).findIndex((w) => w?.uuid === partitionWallUuid);
        if (gi < 0) return prev;
        return finalizePartitionCore(prev, gi, unitType);
      });
    },
    [setBuilding],
  );

  return {
    setBuildingWalls,
    deleteWall,
    updateWallThickness,
    updateWallLength,
    updatePartitionWallLength,
    updatePartitionWallThickness,
    deletePartitionWall,
    updateAllWallLengths,
    addWallNode,
    updateStandardWallAttributes,
    updatePartitionWallAttributes,
    setWallGeometryAndTypeAttributes,
    setPartitionWallGeometryAndTypeAttributes,
    finalizePartitionWallIfSplitsRoom,
    finalizePartitionWallIfSplitsRoomByUuid,
  };
};
